import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTop from "@/components/feature/ScrollTop";
import RankSidebar, { RelatedTopics } from "@/components/feature/RankSidebar";
import NotFound from "@/pages/NotFound";
import { channels, getChannel } from "@/mocks/channels";
import { topics, type TopicComment } from "@/mocks/topics";
import { formatNum } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";

type Vote = "支持" | "中立" | "反对" | null;

export default function TopicDetail() {
  const { slug = "" } = useParams();
  const topic = topics.find((t) => t.slug === slug);
  const [myVote, setMyVote] = useState<Vote>(null);
  const [comments, setComments] = useState<TopicComment[]>(topic ? [...topic.topComments] : []);
  const [input, setInput] = useState("");
  const [sent, setSent] = useState(false);

  useSeo({
    title: topic ? `${topic.title} - 网友评论与舆论风向 | 全网吃瓜` : "话题不存在 | 全网吃瓜",
    description: topic ? topic.summary : "该话题不存在或已被移除。",
    keywords: topic ? topic.tags.join(",") : undefined,
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            keywords: topic.tags.join(","),
            articleSection: getChannel(topic.channel)?.name,
            dateModified: topic.updated,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://coyonmachine.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: getChannel(topic.channel)?.name,
                item: `https://coyonmachine.cn/channels/${topic.channel}`,
              },
              { "@type": "ListItem", position: 3, name: topic.title },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return <NotFound />;
  }

  const ch = channels.find((c) => c.slug === topic.channel) ?? getChannel(topic.channel);
  const voteActive: Record<NonNullable<Vote>, string> = {
    支持: "bg-accent-500 text-background-50",
    中立: "bg-secondary-500 text-background-50",
    反对: "bg-primary-500 text-background-50",
  };

  function handleVote(v: NonNullable<Vote>) {
    setMyVote(myVote === v ? null : v);
  }

  function submitComment() {
    const text = input.trim();
    if (!text) return;
    const newComment: TopicComment = {
      user: "路过吃瓜网友",
      time: "刚刚",
      likes: 0,
      side: "围观",
      text,
    };
    setComments([newComment, ...comments]);
    setInput("");
    setSent(true);
    setTimeout(() => setSent(false), 2000);
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* 头部 */}
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="wrap py-8 md:py-10">
            <nav aria-label="面包屑" className="text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-primary-600">
                    首页
                  </Link>
                </li>
                <li className="text-foreground-300">/</li>
                <li>
                  <Link to={`/channels/${topic.channel}`} className="hover:text-primary-600">
                    {ch?.name ?? "频道"}
                  </Link>
                </li>
                <li className="text-foreground-300">/</li>
                <li className="line-clamp-1 max-w-[14rem] font-medium text-foreground-800">{topic.title}</li>
              </ol>
            </nav>

            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-primary-100 px-2.5 py-1 font-semibold text-primary-700">
                {ch?.label ?? "话题"}
              </span>
              {topic.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-background-200/70 px-2.5 py-1 text-foreground-500">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-display text-2xl font-bold leading-snug text-foreground-900 md:text-4xl">
              {topic.title}
            </h1>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-foreground-600 md:text-lg">{topic.dek}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" />
                热度 {formatNum(topic.heat)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line" aria-hidden="true" />
                {formatNum(topic.comments)} 条讨论
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true" />
                更新于 {topic.updated}
              </span>
            </div>
          </div>
        </section>

        <div className="wrap mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            {/* 封面 */}
            <img
              src={topic.image}
              alt={`${topic.title} - 话题配图`}
              className="h-52 w-full rounded-2xl object-cover md:h-80"
            />

            {/* 表态 */}
            <section className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground-900">你的态度是？</h2>
                  <p className="mt-1 text-sm text-foreground-500">留下你的一票，看看和广大群众同不同频。</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(["支持", "中立", "反对"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => handleVote(v)}
                      className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                        myVote === v ? voteActive[v] : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      <i
                        className={v === "支持" ? "ri-thumb-up-line" : v === "中立" ? "ri-equalizer-line" : "ri-thumb-down-line"}
                        aria-hidden="true"
                      />
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              {myVote && (
                <p className="mt-4 rounded-xl bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-accent-600" aria-hidden="true" /> 你投了
                  <span className="mx-1 font-bold text-primary-700">{myVote}</span>，目前全网 {topic.sentiment.support}% 支持、
                  {topic.sentiment.neutral}% 中立、{topic.sentiment.oppose}% 反对
                </p>
              )}
            </section>

            {/* 舆论风向 */}
            <section className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-900">
                <i className="ri-compass-3-line text-primary-500" aria-hidden="true" />
                舆论风向速览
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{topic.summary}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">人群情绪：</span>
                {topic.mood.map((m) => (
                  <span key={m} className="rounded-full bg-accent-100 px-3 py-1 text-sm font-medium text-accent-900">
                    {m}
                  </span>
                ))}
              </div>

              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                <Bar label="支持" value={topic.sentiment.support} color="bg-accent-500" />
                <Bar label="中立" value={topic.sentiment.neutral} color="bg-secondary-400" />
                <Bar label="反对" value={topic.sentiment.oppose} color="bg-primary-500" />
              </div>

              <div className="mt-7 space-y-4 border-t border-background-200 pt-6">
                <h3 className="text-base font-bold text-foreground-900">来龙去脉</h3>
                {topic.background.map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-foreground-600">
                    {p}
                  </p>
                ))}
              </div>
            </section>

            {/* 各方观点 */}
            <section className="mt-6">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-900">
                <i className="ri-question-answer-line text-primary-500" aria-hidden="true" />
                评论区都怎么说
              </h2>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                {topic.standpoints.map((s) => (
                  <article key={s.name} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-foreground-900">{s.name}</h3>
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800">
                        约 {s.ratio}%
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500">{s.gist}</p>
                    <ul className="mt-3 space-y-2.5">
                      {s.points.map((p, i) => (
                        <li key={i} className="flex gap-2 text-sm leading-relaxed text-foreground-700">
                          <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" aria-hidden="true" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
              <div className="mt-4 rounded-2xl border-l-4 border-primary-400 bg-background-100 p-5">
                <p className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                  <i className="ri-megaphone-line mt-0.5 text-primary-500" aria-hidden="true" />
                  <span>
                    <span className="font-bold text-foreground-900">风向小结：</span>
                    {topic.conclusion}
                  </span>
                </p>
              </div>
            </section>

            {/* 高赞神评 */}
            <section className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-900">
                  <i className="ri-heart-3-line text-primary-500" aria-hidden="true" />
                  高赞神评
                </h2>
                <span className="text-xs text-foreground-400">共 {comments.length + Math.round(topic.comments / 1000)} 条讨论</span>
              </div>

              <div className="mt-4 space-y-3">
                {comments.map((c, i) => (
                  <CommentItem key={`${c.user}-${i}`} comment={c} rank={i} />
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-5">
                <label htmlFor="comment-input" className="text-sm font-semibold text-foreground-800">
                  我也想说两句
                </label>
                <textarea
                  id="comment-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value.slice(0, 300))}
                  maxLength={300}
                  rows={3}
                  placeholder="理性吃瓜、尊重多元。说说你的看法，会有无数和你同频的人～"
                  className="mt-3 w-full resize-none rounded-xl border border-background-200 bg-background-100/70 p-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:bg-background-50"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-foreground-400">{input.length}/300</span>
                  <button
                    type="button"
                    onClick={submitComment}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line" aria-hidden="true" /> 发布评论
                  </button>
                </div>
                {sent && (
                  <p className="mt-3 rounded-lg bg-accent-100 px-3 py-2 text-xs text-accent-900">
                    <i className="ri-check-line" aria-hidden="true" /> 已发布（演示环境，仅本次浏览可见）
                  </p>
                )}
              </div>
            </section>
          </div>

          {/* 侧栏 */}
          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <RankSidebar activeSlug={topic.slug} />
            <RelatedTopics topic={topic} />
            <aside className="card-tint rounded-2xl bg-secondary-100/70 p-5">
              <h3 className="font-display text-lg font-bold text-secondary-900">理性吃瓜倡议</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                观点可以不同，但请就事论事。我们反对网络暴力与造谣带节奏，让讨论保持善意。
              </p>
            </aside>
          </aside>
        </div>
      </main>
      <Footer />
      <ScrollTop />
    </div>
  );
}

function Bar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-xl border border-background-200 bg-background-100/50 p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground-700">{label}</span>
        <span className="font-display text-xl font-bold text-foreground-900">{value}%</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

const sideChip: Record<string, string> = {
  支持: "bg-accent-100 text-accent-900",
  中立: "bg-secondary-100 text-secondary-800",
  反对: "bg-primary-100 text-primary-800",
  围观: "bg-background-200 text-foreground-500",
};

function CommentItem({ comment, rank }: { comment: TopicComment; rank: number }) {
  return (
    <article className="flex gap-3 rounded-2xl border border-background-200 bg-background-50 p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-800">
        {comment.user.slice(0, 1)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground-900">{comment.user}</span>
          {comment.tag && <span className="rounded bg-background-200/70 px-1.5 py-0.5 text-xs text-foreground-500">{comment.tag}</span>}
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sideChip[comment.side]}`}>{comment.side}</span>
          {rank < 3 && (
            <span className="flex items-center gap-0.5 text-xs font-bold text-primary-600">
              <i className="ri-fire-fill" aria-hidden="true" /> 高赞
            </span>
          )}
        </div>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{comment.text}</p>
        <div className="mt-2 flex items-center gap-4 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-thumb-up-line" aria-hidden="true" /> {formatNum(comment.likes)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-message-3-line" aria-hidden="true" /> 回复
          </span>
          <span className="ml-auto">{comment.time}</span>
        </div>
      </div>
    </article>
  );
}